// ISO 3166-1 alpha-2 country codes. Names are resolved (and localized) at
// runtime via Intl.DisplayNames and flags are derived from the code, so this
// list only needs the codes — same approach as utils/currencies.ts.
export const COUNTRY_CODES: string[] = [
  'AD', 'AE', 'AF', 'AG', 'AL', 'AM', 'AO', 'AR', 'AT', 'AU', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BN', 'BO', 'BR', 'BS', 'BT', 'BW', 'BY', 'BZ',
  'CA', 'CD', 'CF', 'CG', 'CH', 'CI', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'ER', 'ES', 'ET',
  'FI', 'FJ', 'FM', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GH', 'GM', 'GN', 'GQ', 'GR', 'GT', 'GW', 'GY',
  'HN', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IN', 'IQ', 'IR', 'IS', 'IT',
  'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MR', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NE', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NZ',
  'OM',
  'PA', 'PE', 'PG', 'PH', 'PK', 'PL', 'PT', 'PW', 'PY',
  'QA',
  'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SI', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SY', 'SZ',
  'TD', 'TG', 'TH', 'TJ', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
  'UA', 'UG', 'US', 'UY', 'UZ',
  'VA', 'VC', 'VE', 'VN', 'VU',
  'WS',
  'YE',
  'ZA', 'ZM', 'ZW',
];

const nameCache = new Map<string, Intl.DisplayNames>();

function displayNames(locale: string): Intl.DisplayNames | null {
  const key = locale || 'en';
  const cached = nameCache.get(key);
  if (cached) return cached;
  try {
    const dn = new Intl.DisplayNames([key], { type: 'region' });
    nameCache.set(key, dn);
    return dn;
  } catch {
    return null;
  }
}

/** Localized display name for an ISO2 country code, falling back to the code. */
export function countryName(code: string, locale = 'en'): string {
  const dn = displayNames(locale);
  try {
    return dn?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** 🇯🇵 flag emoji from an ISO2 code (regional-indicator letters). */
export function flagEmoji(code: string): string {
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map(ch => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

export interface CountryOption {
  code: string;
  name: string;
  flag: string;
}

// Countries travellers most often hold passports from / travel to, floated to
// the top so the picker doesn't open on obscure ones.
const COMMON_CODES = [
  'ES', 'US', 'GB', 'FR', 'DE', 'IT', 'PT', 'NL', 'BE', 'CH', 'IE',
  'CA', 'AU', 'BR', 'MX', 'AR', 'JP', 'CN', 'IN', 'KR',
];

/** Full country list with localized names + flags: common ones first, then the
 *  rest alphabetically by localized name. */
export function countryOptions(locale = 'en'): CountryOption[] {
  const toOption = (code: string): CountryOption => ({ code, name: countryName(code, locale), flag: flagEmoji(code) });
  const common = COMMON_CODES.filter(c => COUNTRY_CODES.includes(c)).map(toOption);
  const rest = COUNTRY_CODES
    .filter(c => !COMMON_CODES.includes(c))
    .map(toOption)
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  return [...common, ...rest];
}

/** Best-effort guess of the user's nationality from the browser locale. Defaults to ES. */
export function guessCountryFromLocale(locale?: string): string {
  const lang = locale || (typeof navigator !== 'undefined' ? navigator.language : '') || '';
  try {
    const region = new Intl.Locale(lang).maximize().region;
    if (region && COUNTRY_CODES.includes(region)) return region;
  } catch {
    // ignore — fall through to default
  }
  return 'ES';
}
